// vim: set ts=2 sts=2 sw=2 et:

import type { Equipment, Event } from "../../../types/dataTypes";
import type { PlatformLiveAttempt } from "../../live/platformLiveProtocol";
import { countryLabel } from "./countryLabel";
import { buildAthleteView, formatKg, LIFT_LABELS, OverlayAthleteView } from "./overlayView";

export interface FichaFact {
  label: string;
  value: string;
}

export interface FichaPersonalBest {
  label: string;
  value: string;
}

export interface FichaView extends OverlayAthleteView {
  modality: string | null;
  origin: Array<string>;
  facts: Array<FichaFact>;
  personalBests: Array<FichaPersonalBest>;
  photoUrl: string | null;
}

const EVENT_LABELS: Readonly<Record<Event, string>> = {
  SBD: "Full Powerlifting",
  S: "Sentadilla",
  B: "Press de Banca",
  D: "Peso Muerto",
  SB: "Sentadilla y Banca",
  SD: "Sentadilla y Peso Muerto",
  BD: "Push & Pull",
};

const EQUIPMENT_LABELS: Readonly<Partial<Record<Equipment, string>>> = {
  Bare: "RAW",
  Sleeves: "RAW",
  Wraps: "Vendas",
};

// Nombres de las modalidades de la web (apps/nextjs/src/utils/constants.ts `events`), espejados a propósito:
// los repos no se importan entre sí.
const WRPF_EVENT_LABELS: Readonly<Record<string, string>> = {
  SBD_RAW: "Full Powerlifting RAW",
  SBD_CLASSIC: "Full Powerlifting CLASSIC",
  SBD_SINGLE_PLY: "Full Powerlifting Single-Ply",
  SBD_MULTI_PLY: "Full Powerlifting Multi-Ply",
  S: "Squat Only RAW",
  S_CLASSIC: "Squat Only Classic",
  S_SINGLE_PLY: "Squat Only Single-Ply",
  S_MULTI_PLY: "Squat Only Multi-Ply",
  BD: "Push-Pull RAW",
  BD_SINGLE_PLY: "Push-Pull Single-Ply",
  BD_MULTI_PLY: "Push-Pull Multi-Ply",
  B: "Bench Only RAW",
  B_SINGLE_PLY: "Bench Only Single-Ply",
  B_MULTI_PLY: "Bench Only Multi-Ply",
  D: "Deadlift Only RAW",
  D_SINGLE_PLY: "Deadlift Only Single-Ply",
  D_MULTI_PLY: "Deadlift Only Multi-Ply",
};

// La modalidad de la web manda (su código se traduce al nombre que usa la web; un texto ya legible se muestra
// tal cual); sin ella se arma con los eventos de OpenLifter.
const getModality = (attempt: PlatformLiveAttempt): string | null => {
  const meta = attempt.athleteMeta;
  const webEvent = meta?.primaryClassification?.event?.trim();
  if (webEvent) return WRPF_EVENT_LABELS[webEvent] ?? webEvent;
  const events = meta?.events ?? [];
  if (events.length === 0) return null;
  const equipment = meta?.equipment === undefined ? undefined : EQUIPMENT_LABELS[meta.equipment];
  return [events.map((event) => EVENT_LABELS[event]).join(" + "), equipment].filter(Boolean).join(" ");
};

const getOrigin = (attempt: PlatformLiveAttempt): Array<string> => {
  const meta = attempt.athleteMeta;
  const city = meta?.location?.city?.trim();
  const state = meta?.location?.state?.trim();
  const place = city && state && city !== state ? `${city}, ${state}` : city || state;
  const instagram = meta?.instagram?.trim().replace(/^@/, "");
  return [
    countryLabel(meta?.location?.country) ?? undefined,
    place,
    meta?.team?.trim(),
    instagram ? `@${instagram}` : undefined,
  ].filter((value): value is string => typeof value === "string" && value !== "");
};

const getWeightClass = (weightClassKg: string | undefined): string | null => {
  const value = weightClassKg?.trim().replace(/\s*kg$/i, "");
  if (!value) return null;
  if (value.endsWith("+")) return `+${value.slice(0, -1)} kg`;
  return `−${value.replace(/^-/, "")} kg`;
};

export const buildFichaView = (attempt: PlatformLiveAttempt): FichaView => {
  const meta = attempt.athleteMeta;
  const facts: Array<FichaFact | null> = [
    meta?.ageDivision?.trim() ? { label: "Categoría", value: meta.ageDivision.trim() } : null,
    getWeightClass(meta?.weightClassKg) === null
      ? null
      : { label: "Categoría de peso", value: getWeightClass(meta?.weightClassKg) as string },
    meta?.bodyweightKg !== undefined && meta.bodyweightKg > 0
      ? { label: "Peso corporal", value: `${formatKg(meta.bodyweightKg)} kg` }
      : null,
    meta !== undefined && meta.age > 0 ? { label: "Edad", value: `${meta.age} años` } : null,
  ];
  const bests = meta?.personalBestsKg;
  const personalBests: Array<FichaPersonalBest | null> = [
    bests?.squat ? { label: LIFT_LABELS.S, value: formatKg(bests.squat) } : null,
    bests?.bench ? { label: LIFT_LABELS.B, value: formatKg(bests.bench) } : null,
    bests?.deadlift ? { label: LIFT_LABELS.D, value: formatKg(bests.deadlift) } : null,
  ];
  // La ficha sigue al intento actual apenas se resuelve el anterior (immediate-next): no hay veredicto que aplicar.
  const athlete = buildAthleteView(attempt, null);
  // A diferencia del overlay, la ficha muestra el total también a los especialistas (un solo movimiento),
  // a pedido del cliente. Sin liftSeries (operador viejo) la serie es solo el movimiento actual: no hay total.
  const progress =
    attempt.liftSeries !== undefined ? { ...athlete.progress, showTotal: true } : athlete.progress;
  return {
    ...athlete,
    progress,
    modality: getModality(attempt),
    origin: getOrigin(attempt),
    facts: facts.filter((fact): fact is FichaFact => fact !== null),
    personalBests: personalBests.filter((best): best is FichaPersonalBest => best !== null),
    photoUrl: meta?.photoUrl?.trim() || null,
  };
};
